/**
 * Data Fetch API Service
 *
 * Domain-specific API service for data fetch request operations
 * Uses httpClient for all HTTP calls to ensure:
 * - Automatic token attachment
 * - Token rotation handling
 * - Centralized error handling
 */

import { httpClient } from './http-client.service';
import type {
  DataFetchListResponse,
  DataFetchQueryParams,
} from '../types/data-fetch-request';

class DataFetchAPIService {
  /**
   * Get all data fetch requests with pagination
   *
   * @param params Query parameters (limit, pageno, consentId)
   * @returns Promise with data fetch request list
   */
  async getAllDataFetchRequests(params: DataFetchQueryParams): Promise<DataFetchListResponse> {
    try {
      const response = await httpClient.post<DataFetchListResponse>(
        'consent/dataFetchRequest/list',
        params
      );
      return response.data;
    } catch (error) {
      console.error('[DataFetchAPI] Failed to fetch data fetch requests:', error);
      throw error;
    }
  }

  /**
   * Get data fetch requests by consent ID
   *
   * @param consentId Consent ID to filter by
   * @param params Additional query parameters
   * @returns Promise with filtered data fetch request list
   */
  async getDataFetchByConsentId(
    consentId: string,
    params: Omit<DataFetchQueryParams, 'consentId'>
  ): Promise<DataFetchListResponse> {
    try {
      const response = await httpClient.post<DataFetchListResponse>(
        'consent/dataFetchRequest/list',
        { ...params, consentId }
      );
      return response.data;
    } catch (error) {
      console.error('[DataFetchAPI] Failed to fetch data fetch requests by consent ID:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const dataFetchAPI = new DataFetchAPIService();
